import {
  FormCheckbox,
  FormInput,
  FormSelect,
  Heading,
  Loader,
  MonthlyDistribution,
} from "@/components";
import useAddEditLineItem from "./useAddEditLineItem";
import { FormProvider } from "react-hook-form";
import {
  AUTO_APPROVAL,
  AUTO_APPROVAL_LEVEL,
  autoApprovalLevelOptions,
  BUDGET_QTY,
  FINANCIAL_YEAR,
  GL_MASTER_ID,
  TOLERANCE,
  TRANSACTION_RATE,
  YEARLY_AMT,
} from "./consts";
import { Button } from "@/components/ui";

const AddEditLineItem = () => {
  const {
    breadcrumbData,
    onBackClick,
    isView,
    onSubmit,
    form,
    requiredFields,
    onCancel,
    isAddPending,
    isUpdatePending,
    isLineItemDataLoading,
    isBudgetDataLoading,
    isLineItemListLoading,
    glMasterListing,
    isGlMasterLoading,
    budgetData,
    isAdd,
    onMontyDistributionClick,
    openDistributionDialog,
    setOpenDistributionDialog,
    glKey,
    isAddDistributionPending,
    isUpdateDistributionPending,
    onSaveMonthlyDistribution,
    approveLevelKey,
    storedLineItemList,
    isDistributionLoading,
    monthlyDistribution,
  } = useAddEditLineItem();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6 !pt-0">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between">
        <Heading
          title={
            <div className="flex items-center">
              {isAdd
                ? `Add Line Item`
                : isView
                ? `View Line Item`
                : `Edit Line Item`}
              <span className="text-sm text-primary font-bold ml-3">
                Budget - {budgetData?.budgetName}{" "}
                <span className="font-normal">|</span> Financial Year -{" "}
                {budgetData?.year} <span className="font-normal">|</span>{" "}
                Version - {budgetData?.versionNo}
              </span>
            </div>
          }
          breadcrumbData={breadcrumbData}
          showBack
          onBackClick={onBackClick}
        />
      </div>

      {isLineItemDataLoading || isGlMasterLoading || isDistributionLoading || isBudgetDataLoading || isLineItemListLoading ? (
        <Loader />
      ) : (
        <>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                  <FormInput
                    name={FINANCIAL_YEAR}
                    control={form.control}
                    label="Financial Year"
                    placeholder="Please enter"
                    error={errors[FINANCIAL_YEAR]?.message}
                    isRequired
                    disabled
                  />
                  <FormSelect
                    key={glKey}
                    name={GL_MASTER_ID}
                    control={form.control}
                    label="GL Entry"
                    placeholder="Please select"
                    error={errors[GL_MASTER_ID]?.message}
                    isRequired={requiredFields.includes(GL_MASTER_ID)}
                    disabled={isView}
                    options={(glMasterListing?.data || [])
                      ?.sort((a: any, b: any) =>
                        a.glAccountNumber?.localeCompare(b.glAccountNumber)
                      )
                      ?.map((data: any) => {
                        const storedIds = new Set(
                          storedLineItemList?.map(
                            (item: any) => item?.glMasterId
                          ) || []
                        );
                        return {
                          label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
                          value: data?.glMasterId?.toString(),
                          disabled: storedIds.has(data?.glMasterId),
                        };
                      })
                      ?.sort((a: any, b: any) => {
                        return a.disabled === b.disabled
                          ? 0
                          : a.disabled
                          ? 1
                          : -1;
                      })}
                  />
                  <FormInput
                    name={TRANSACTION_RATE}
                    control={form.control}
                    label="Rate"
                    placeholder="Please enter"
                    error={errors[TRANSACTION_RATE]?.message}
                    isRequired={requiredFields.includes(TRANSACTION_RATE)}
                    disabled={isView}
                    variant="integer"
                    startIcon={<span>$</span>}
                  />
                  <FormInput
                    name={BUDGET_QTY}
                    control={form.control}
                    label="Quantity"
                    placeholder="Please enter"
                    error={errors[BUDGET_QTY]?.message}
                    isRequired={requiredFields.includes(BUDGET_QTY)}
                    disabled={isView}
                    variant="integer"
                  />
                  <FormInput
                    name={TOLERANCE}
                    control={form.control}
                    label="Tolerance %"
                    placeholder="Please enter"
                    error={errors[TOLERANCE]?.message}
                    isRequired={requiredFields.includes(TOLERANCE)}
                    disabled={isView}
                    maxLength={3}
                    variant="integer"
                  />
                  <div>
                    <FormInput
                      name={YEARLY_AMT}
                      control={form.control}
                      label="Annual Budget"
                      placeholder="Please enter"
                      error={errors[YEARLY_AMT]?.message}
                      isRequired={requiredFields.includes(YEARLY_AMT)}
                      disabled={isView}
                      variant="integer"
                      startIcon={<span>$</span>}
                    />
                    {!!form.watch(YEARLY_AMT) && (
                      <div className="flex items-end w-full justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          className="shadow-none w-fit p-0 text-[12px] h-[20px] text-primary border-b-primary border-b-1 rounded-none hover:bg-transparent hover:text-primary"
                          onClick={onMontyDistributionClick}
                        >
                          Monthly Distribution
                        </Button>
                      </div>
                    )}
                  </div>
                  <FormCheckbox
                    name={AUTO_APPROVAL}
                    control={form.control}
                    label="Auto Approval"
                    error={errors[AUTO_APPROVAL]?.message}
                    isRequired={requiredFields.includes(AUTO_APPROVAL)}
                    disabled={isView}
                  />
                  {form.watch(AUTO_APPROVAL) && (
                    <FormSelect
                      key={approveLevelKey}
                      name={AUTO_APPROVAL_LEVEL}
                      control={form.control}
                      label="Auto Approval Level"
                      placeholder="Please select"
                      error={errors[AUTO_APPROVAL_LEVEL]?.message}
                      isRequired={!!form.watch(AUTO_APPROVAL)}
                      disabled={isView}
                      options={autoApprovalLevelOptions}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-row justify-end gap-3  py-3">
                <Button
                  variant="outline"
                  type="button"
                  onClick={onCancel}
                  disabled={
                    isAddPending ||
                    isUpdatePending ||
                    isAddDistributionPending ||
                    isUpdateDistributionPending
                  }
                >
                  Cancel
                </Button>
                {!isView && (
                  <Button
                    type="submit"
                    disabled={
                      isAddPending ||
                      isUpdatePending ||
                      isAddDistributionPending ||
                      isUpdateDistributionPending
                    }
                  >
                    {isAddPending ||
                      isUpdatePending ||
                      isAddDistributionPending ||
                      (isUpdateDistributionPending && (
                        <Loader variant="secondary" />
                      ))}
                    Save
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </>
      )}
      {openDistributionDialog && (
        <MonthlyDistribution
          openDistributionDialog={openDistributionDialog}
          setOpenDistributionDialog={setOpenDistributionDialog}
          draftDistributionData={monthlyDistribution}
          annualBudget={form.watch(YEARLY_AMT)}
          onSave={onSaveMonthlyDistribution}
          isView={!!isView}
        />
      )}
    </div>
  );
};

export default AddEditLineItem;
